import { Dispatch } from "react";
import {
  Customer,
  Origin,
  PointOfSale,
  InitialData,
  ComplementaryData,
  Payment,
  DPS,
  ProposalResponse,
  ChangeBeneficiariesDispatch,
  Cancellation,
  Plan,
  JointAccount,
  Beneficiary,
  CustomerData,
  SimulationMobi,
  Geolocation,
  Modality,
  ProposalImplantation,
  MultipleContractedInsurance,
  Channel,
  ContextSpouse,
} from "#/api/Models";
import { ComposedValues } from "#/containers/DPS/Questionnarie";

export interface ContextProps<T, P> {
  state: T;
  dispatch: Dispatch<P>;
}

export enum ActionTypes {
  SET_CUSTOMER = "SET_CUSTOMER",
  SET_ORIGIN = "SET_ORIGIN",
  SET_CHANNEL = "SET_CHANNEL",
  SET_POINT_OF_SALE = "SET_POINT_OF_SALE",
  SET_INITIAL_DATA = "SET_INITIAL_DATA",
  SET_NOT_ASSOCIATED = "SET_NOT_ASSOCIATED",
  SET_SIMULATION = "SET_SIMULATION",
  SET_COMPLEMENTARY_DATA = "SET_COMPLEMENTARY_DATA",
  SET_SPOUSE = "SET_SPOUSE",
  SET_DPS = "SET_DPS",
  SET_PAYMENT = "SET_PAYMENT",
  SET_BENEFICIARY = "SET_BENEFICIARY",
  SET_HOLDER_DPS_ANSWERS = "SET_HOLDER_DPS_ANSWERS",
  SET_CONJUGE_DPS_ANSWERS = "SET_CONJUGE_DPS_ANSWERS",
  SET_DPS_STATUS = "SET_DPS_STATUS",
  SET_PROPOSAL = "SET_PROPOSAL",
  SET_CHANGE_BENEFICIARIES = "SET_CHANGE_BENEFICIARIES",
  SET_CANCELLATION = "SET_CANCELLATION",
  SET_PLAN = "SET_PLAN",
  SET_JOINT_ACCOUNT = "SET_JOINT_ACCOUNT",
  SET_CUSTOMER_DATA = "SET_CUSTOMER_DATA",
  SET_GEOLOCATION = "SET_GEOLOCATION",
  SET_MODALITY = "SET_MODALITY",
  SET_CONTRACTED_INSURANCE_SINGLE = "SET_CONTRACTED_INSURANCE_SINGLE",
  SET_CONTRACTED_INSURANCE_MULTIPLE = "SET_CONTRACTED_INSURANCE_MULTIPLE",
  SET_PDF = "SET_PDF",
  SET_CANCEL = "SET_CANCEL",
}

export type Status = "inFilling" | "filled" | "notFilled";

export type DPSStatus = {
  dpsType?: string;
  holder: Status;
  spouse: Status;
};

export type GlobalAction =
  | { type: ActionTypes.SET_CUSTOMER; payload: Customer }
  | { type: ActionTypes.SET_ORIGIN; payload: Origin }
  | { type: ActionTypes.SET_CHANNEL; payload: Channel }
  | { type: ActionTypes.SET_POINT_OF_SALE; payload: PointOfSale }
  | { type: ActionTypes.SET_INITIAL_DATA; payload: InitialData }
  | { type: ActionTypes.SET_NOT_ASSOCIATED; payload: boolean }
  | { type: ActionTypes.SET_SIMULATION; payload: SimulationMobi }
  | { type: ActionTypes.SET_COMPLEMENTARY_DATA; payload: ComplementaryData }
  | { type: ActionTypes.SET_HOLDER_DPS_ANSWERS; payload: ComposedValues }
  | { type: ActionTypes.SET_CONJUGE_DPS_ANSWERS; payload: ComposedValues }
  | { type: ActionTypes.SET_SPOUSE; payload: ContextSpouse }
  | { type: ActionTypes.SET_DPS; payload: DPS }
  | { type: ActionTypes.SET_PAYMENT; payload: Payment }
  | { type: ActionTypes.SET_BENEFICIARY; payload: Beneficiary[] }
  | { type: ActionTypes.SET_DPS_STATUS; payload: DPSStatus }
  | { type: ActionTypes.SET_PROPOSAL; payload: ProposalResponse }
  | {
      type: ActionTypes.SET_CHANGE_BENEFICIARIES;
      payload: ChangeBeneficiariesDispatch;
    }
  | { type: ActionTypes.SET_CANCELLATION; payload: Cancellation }
  | { type: ActionTypes.SET_PLAN; payload: Plan | undefined }
  | { type: ActionTypes.SET_JOINT_ACCOUNT; payload: JointAccount }
  | { type: ActionTypes.SET_CUSTOMER_DATA; payload: CustomerData }
  | { type: ActionTypes.SET_GEOLOCATION; payload: Geolocation }
  | { type: ActionTypes.SET_MODALITY; payload: Modality }
  | {
      type: ActionTypes.SET_CONTRACTED_INSURANCE_SINGLE;
      payload: ProposalImplantation;
    }
  | {
      type: ActionTypes.SET_CONTRACTED_INSURANCE_MULTIPLE;
      payload: MultipleContractedInsurance;
    }
  | { type: ActionTypes.SET_PDF; payload: Blob }
  | { type: ActionTypes.SET_CANCEL; payload: string | undefined };

export interface GlobalState {
  customer: Customer;
  complementaryData: ComplementaryData;
  origin: string;
  channel: Channel;
  pointOfSale: PointOfSale;
  initialData: InitialData;
  notAssociated: boolean;
  simulation: SimulationMobi;
  spouse: ContextSpouse;
  DPS: DPS;
  holderDPSAnswers: ComposedValues;
  conjugeDPSAnswers: ComposedValues;
  DPSStatus: DPSStatus;
  payment: Payment;
  beneficiaries: Beneficiary[];
  changeBeneficiaries: ChangeBeneficiariesDispatch;
  proposal: ProposalResponse;
  cancellation: Cancellation;
  plan: Plan | undefined;
  jointAccount: JointAccount;
  customerData: CustomerData;
  geolocation?: Geolocation;
  modality: Modality;
  contractedInsuranceSingle: ProposalImplantation;
  contractedInsuranceMultiple: MultipleContractedInsurance;
  pdf: Blob;
  cancel: string | undefined;
}

export interface GlobalContextProps
  extends ContextProps<GlobalState, GlobalAction> {}
